import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card } from '@/components/ui/Card';
import type { AdminTheme } from '@/context/AdminThemeContext';
import type { AdminTransactionStats } from '@/types/transactions';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { getApexChartTheme } from '@/utils/chartTheme';
import { formatTransactionNaira } from '@/utils/transactionFormatters';

function formatDayLabel(day: string) {
  const date = new Date(`${day}T12:00:00`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
}

function formatCurrencyShort(value: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

interface AdminAnalyticsChartsProps {
  stats: AdminTransactionStats;
  theme: AdminTheme;
}

export function AdminAnalyticsCharts({ stats, theme }: AdminAnalyticsChartsProps) {
  const shell = adminShell(theme);
  const isDark = theme === 'dark';

  const dayRows = useMemo(
    () => [...(stats.byDay ?? [])].reverse().slice(-14),
    [stats.byDay],
  );

  const profitTrendOptions: ApexOptions = useMemo(
    () => ({
      ...getApexChartTheme(isDark),
      chart: {
        type: 'area',
        height: 340,
        toolbar: { show: true },
        zoom: { enabled: false },
        fontFamily: 'inherit',
        background: 'transparent',
      },
      colors: ['#10B981', '#3B82F6'],
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05 },
      },
      xaxis: {
        categories: dayRows.map((row) => formatDayLabel(row.day)),
        labels: { rotate: -35 },
      },
      yaxis: {
        labels: { formatter: (value: number) => formatCurrencyShort(value) },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (value: number) => formatTransactionNaira(value) },
      },
      legend: { position: 'top', horizontalAlign: 'right' },
    }),
    [dayRows, isDark],
  );

  const profitTrendSeries = useMemo(
    () => [
      { name: 'Profit', data: dayRows.map((row) => row.profit) },
      { name: 'Debit volume', data: dayRows.map((row) => row.volume) },
    ],
    [dayRows],
  );

  const volumeByCategoryOptions: ApexOptions = useMemo(
    () => ({
      ...getApexChartTheme(isDark),
      chart: {
        type: 'bar',
        height: 320,
        toolbar: { show: false },
        fontFamily: 'inherit',
        background: 'transparent',
      },
      colors: ['#6366F1'],
      plotOptions: {
        bar: { borderRadius: 6, columnWidth: '52%' },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: (stats.byCategory ?? []).map((row) => row.label),
      },
      yaxis: {
        labels: { formatter: (value: number) => formatCurrencyShort(value) },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (value: number) => formatTransactionNaira(value) },
      },
    }),
    [isDark, stats.byCategory],
  );

  const volumeByCategorySeries = useMemo(
    () => [{ name: 'Volume', data: (stats.byCategory ?? []).map((row) => row.volume) }],
    [stats.byCategory],
  );

  const profitDonutOptions: ApexOptions = useMemo(
    () => ({
      ...getApexChartTheme(isDark),
      chart: {
        type: 'donut',
        height: 320,
        fontFamily: 'inherit',
        background: 'transparent',
      },
      colors: ['#10B981', '#3B82F6', '#8B5CF6', '#F97316', '#14B8A6'],
      labels: (stats.byCategory ?? []).map((row) => row.label),
      dataLabels: { enabled: false },
      legend: { position: 'bottom' },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (value: number) => formatTransactionNaira(value) },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total profit',
                formatter: () => formatCurrencyShort(stats.transactions.totalProfit),
              },
            },
          },
        },
      },
    }),
    [isDark, stats.byCategory, stats.transactions.totalProfit],
  );

  const profitDonutSeries = useMemo(
    () => (stats.byCategory ?? []).map((row) => row.profit),
    [stats.byCategory],
  );

  const categoryRows = stats.byCategory ?? [];

  return (
    <>
      <Card className={cn('p-5 sm:p-6', shell.card)}>
        <h2 className="text-lg font-bold">Profit & activity trend</h2>
        <p className={cn('mb-4 text-sm', shell.cardMuted)}>
          Daily profit, debit volume, and transaction count
        </p>
        {dayRows.length === 0 ? (
          <p className={cn('flex h-72 items-center justify-center text-sm', shell.cardMuted)}>
            No activity data for this period
          </p>
        ) : (
          <ReactApexChart options={profitTrendOptions} series={profitTrendSeries} type="area" height={340} />
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className={cn('p-5 sm:p-6', shell.card)}>
          <h2 className="text-lg font-bold">Volume by category</h2>
          <p className={cn('mb-4 text-sm', shell.cardMuted)}>Successful debit volume per service type</p>
          {categoryRows.length === 0 ? (
            <p className={cn('flex h-64 items-center justify-center text-sm', shell.cardMuted)}>No data yet</p>
          ) : (
            <ReactApexChart
              options={volumeByCategoryOptions}
              series={volumeByCategorySeries}
              type="bar"
              height={320}
            />
          )}
        </Card>

        <Card className={cn('p-5 sm:p-6', shell.card)}>
          <h2 className="text-lg font-bold">Profit mix</h2>
          <p className={cn('mb-4 text-sm', shell.cardMuted)}>Share of profit across categories</p>
          {categoryRows.length === 0 || profitDonutSeries.every((v) => v === 0) ? (
            <p className={cn('flex h-64 items-center justify-center text-sm', shell.cardMuted)}>No profit data yet</p>
          ) : (
            <ReactApexChart options={profitDonutOptions} series={profitDonutSeries} type="donut" height={320} />
          )}
        </Card>
      </div>

      {categoryRows.length > 0 && (
        <Card className={cn('overflow-x-auto p-5 sm:p-6', shell.card)}>
          <h2 className="mb-4 text-lg font-bold">Category breakdown</h2>
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className={cn('border-b text-left text-xs uppercase tracking-wide', shell.tableHead, shell.tableWrap)}>
                <th className="px-3 py-3 font-semibold">Category</th>
                <th className="px-3 py-3 text-right font-semibold">Transactions</th>
                <th className="px-3 py-3 text-right font-semibold">Volume</th>
                <th className="px-3 py-3 text-right font-semibold">Profit</th>
              </tr>
            </thead>
            <tbody>
              {categoryRows.map((row) => (
                <tr key={row.category} className={cn('border-b', shell.tableRow, shell.tableWrap)}>
                  <td className={cn('px-3 py-3 font-medium', shell.tableCell)}>{row.label}</td>
                  <td className={cn('px-3 py-3 text-right tabular-nums', shell.tableCell)}>{row.count}</td>
                  <td className={cn('px-3 py-3 text-right tabular-nums', shell.tableCell)}>
                    {formatTransactionNaira(row.volume)}
                  </td>
                  <td className="px-3 py-3 text-right font-semibold tabular-nums text-emerald-500">
                    {formatTransactionNaira(row.profit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
